import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, User, Building2, FolderOpen, UserCheck } from 'lucide-react';
import TimeEntryCell from './TimeEntryCell';

const GroupedCalendarCell = ({ 
  entries, 
  groupBy, 
  clients, 
  projects, 
  teamMembers, 
  onEdit, 
  onDuplicate, 
  onView, 
  onStatusUpdate, 
  onAddComment, 
  onUpdateTags, 
  userRole, 
  isDayView = false, 
  isCompact = false, 
  maxEntriesPerGroup = 3 
}) => {
  const getGroupedEntries = () => {
    const groups = {};
    entries.forEach(entry => {
      let groupKey, groupLabel, Icon;
      switch (groupBy) {
        case 'user': {
          groupKey = entry.user_id;
          groupLabel = entry.user_name || 'Unknown User';
          Icon = User;
          break;
        }
        case 'role': {
          groupKey = entry.bill_rate_role || 'unassigned';
          groupLabel = entry.bill_rate_role 
            ? entry.bill_rate_role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : 'Unassigned';
          Icon = UserCheck;
          break;
        }
        case 'client': {
          groupKey = entry.client_id || 'no_client';
          groupLabel = entry.client_name || 'No Client';
          Icon = Building2;
          break;
        }
        case 'project': {
          groupKey = entry.project_id || 'no_project';
          groupLabel = entry.project_name || 'No Project';
          Icon = FolderOpen;
          break;
        }
        default: {
          return; // Fallback, no grouping
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = { 
          label: groupLabel, 
          Icon,
          entries: [], 
          totalHours: 0 
        };
      }
      groups[groupKey].entries.push(entry);
      groups[groupKey].totalHours += entry.duration_hours || 0;
    });
    
    return Object.entries(groups).sort((a, b) => b[1].totalHours - a[1].totalHours);
  };

  const groupedData = getGroupedEntries();

  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const next = {};
    groupedData.forEach(([key]) => { next[key] = isDayView ? true : false; }); // Expand all in day view, collapse in mini-views
    setExpanded(next);
  }, [entries, groupBy, isDayView]);

  const toggleOne = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const getProject = (projectId) => projects.find(p => p.id === projectId);
  const getClient = (clientId) => clients.find(c => c.id === clientId);

  if (groupedData.length === 0) return null;

  return (
    <div className={`space-y-${isCompact ? '1' : '4'}`}>
      {groupedData.map(([groupKey, groupData]) => {
        const visibleEntries = groupData.entries.slice(0, maxEntriesPerGroup);
        const hasMore = groupData.entries.length > maxEntriesPerGroup;
        
        return (
          <div key={groupKey} className={`${isCompact ? 'p-1 bg-slate-50 rounded' : 'bg-slate-50 rounded-lg p-3'}`}>
            {/* Group Header */}
            <div className={`flex items-center justify-between mb-${isCompact ? '1' : '2'}`}>
              <div className="flex items-center space-x-1">
                <groupData.Icon className={`w-3 h-3 ${isCompact ? 'text-slate-500' : 'text-blue-600'}`} />
                <span className={`text-xs font-medium ${isCompact ? 'text-slate-600' : 'text-slate-700'}`}>
                  {groupData.label} ({formatTime(groupData.totalHours)})
                </span>
              </div>
              {!isCompact && (
                <button
                  onClick={() => toggleOne(groupKey)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  {expanded[groupKey] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
            </div>

            {/* Group Entries */}
            {(isCompact || expanded[groupKey]) && (
              <div className={`space-y-${isCompact ? '0.5' : '2'}`}>
                {visibleEntries.map(entry => (
                  <TimeEntryCell
                    key={entry.id}
                    entry={entry}
                    project={getProject(entry.project_id)}
                    client={getClient(entry.client_id)}
                    onEdit={onEdit}
                    onDuplicate={onDuplicate}
                    onView={onView}
                    isCompact={isCompact || !isDayView}
                  />
                ))}
                {hasMore && (
                  <div className={`text-xs text-slate-500 text-center py-0.5 ${isCompact ? 'italic' : ''}`}>
                    +{groupData.entries.length - maxEntriesPerGroup} more
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GroupedCalendarCell;